import React, {useMemo, useState, useContext, useEffect, useCallback} from 'react';
import {List, Set, Map} from 'immutable';
import makeConstants from 'lib/makeConstants';
import {
  Tab,
  TabNavigation,
  Dialogue,
  Button,
  SearchInput,
  Modal,
  Icon,
  IconButton,
  LoadingSpinner,
  Text,
  ListGroup,
  ListGroupItem,
  Badge
} from '@albert-io/atomic';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import {useTracking} from 'react-tracking';
import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';
import classnames from 'classnames';
import {GuideLevelObjectType} from './GuideLevel.helpers';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';

import sessionStore from 'client/Session/SessionStore';

import SubjectsFilter from './SubjectsFilter';
import SearchResults from './SearchResults.react';
import {
  getClassroomSubjectsQuery,
  getClassroomsQuery,
  getGuideQuestionCountsQuery,
  getSearchResults
} from './TopicsModal.queries';
import './topics-modal.scss';

const guideTypes = ['practice', 'assessment', 'free_response'] as const;

const guideType = makeConstants(...guideTypes);

type GuideTypes = (typeof guideTypes)[number];

interface TopicsModalProps {
  handleClose: () => void;
}

const guideTypeText = {
  [guideType.practice]: 'Practice',
  [guideType.assessment]: 'Assessment',
  [guideType.free_response]: 'FRQ'
};

const TopicsModal = ({handleClose}: TopicsModalProps) => {
  const [activeGuideType, setActiveType] = useState<GuideTypes>(guideType.practice);
  const {Track, trackEvent} = useTracking({
    component: 'topics modal'
  });
  const [recentSubjects, setRecentSubjects] = useState<List<SubjectModelV2>>(List());
  const [loadingClassSubjects, setLoadingClassSubjects] = useState(false);
  const [guideCounts, setGuideCounts] = useState(Map());

  const {
    setSelectedGuideLevels,
    searchString,
    setSearchString,
    selectedTopicsModalSubjects,
    setSelectedTopicsModalSubjects,
    guideCollection,
    setGuideCollection,
    questionDifficulties,
    questionTypes,
    standardCollection,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    getSelectedStandardsAndSets
  } = useContext(ContentDiscoveryContext);

  const {selectedStandardIds, selectedStandardSetIds} =
    getSelectedStandardsAndSets(standardCollection);

  const onTabSelection = (tab: GuideTypes) => {
    setActiveType(tab);
    trackEvent({type: 'tab', event: 'click', content: tab});
  };

  const isEmpty = useMemo(() => !selectedTopicsModalSubjects.size, [selectedTopicsModalSubjects]);

  const getSelectedGuideLevelsInSession = () => {
    let selectedGuideLevelsInSession = List<GuideLevelModelV2>();

    guideCollection.toList().forEach((guide) => {
      selectedGuideLevelsInSession = List([
        ...selectedGuideLevelsInSession.toArray(),
        ...getSelectedGuideElements(guide)
      ]);
    });
    return selectedGuideLevelsInSession;
  };

  const isGuideLevelSelected = (guideLevelObj: GuideLevelObjectType) => {
    return guideLevelObj.isSelected && !guideLevelObj.isIndeterminate;
  };

  // We only want to return guide levels that do not have a selected parent.
  const getSelectedGuideElements = (guideLevelMap) => {
    return guideLevelMap.reduce((acc, guideLevelObj) => {
      const parentObj = guideLevelObj.guideLevel.getParentId()
        ? guideLevelMap.get(guideLevelObj.guideLevel.getParentId())
        : null;
      // We will add selected parent in a different iteration.
      if (parentObj && isGuideLevelSelected(parentObj)) {
        return acc;
      }
      // If parent is not selected but this element is, add it.
      if (isGuideLevelSelected(guideLevelObj)) {
        acc.push(guideLevelObj.guideLevel);
      }
      // If element is not in any selection state, skip it.
      return acc;
    }, []);
  };

  const onSubmit = () => {};

  const {
    isReady,
    resources: [classrooms]
  } = useMandarkQueries([getClassroomsQuery()]);

  const classroomIds = useMemo(
    () => classrooms.map((classroom) => classroom.getId()).toArray(),
    [classrooms]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoadingClassSubjects(true);
      const query = getClassroomSubjectsQuery('', classroomIds.join(','), 6);
      const result = await query.getResourcePromise();
      setRecentSubjects(result);
      setLoadingClassSubjects(false);
    };
    if (isReady && classrooms.size > 0) {
      fetchData();
    }
  }, [classroomIds, isReady]);

  const checkIsAssesmentOnly = useCallback(
    async (selectedSubjects) => {
      if (activeGuideType === guideType.practice && selectedSubjects.size > 0) {
        const subjects = selectedSubjects.map((subject) => subject.getId()).join(',');

        const assessmentsPromise = getSearchResults(subjects, '', guideType.assessment, 5);
        const practicesPromise = getSearchResults(subjects, '', guideType.practice, 5);
        const [assesments, practices] = await Promise.all([assessmentsPromise, practicesPromise]);

        if (practices.isEmpty() && assesments.size > 0) {
          setActiveType(guideType.assessment);
        }
      }
    },
    [activeGuideType, setActiveType]
  );

  const selectHandler = useCallback(
    (item: SubjectModelV2) => {
      setSelectedTopicsModalSubjects((prevSelected) => {
        const shouldRemove = prevSelected.includes(item);
        const updatedSelected = shouldRemove ? prevSelected.delete(item) : prevSelected.add(item);
        checkIsAssesmentOnly(updatedSelected);
        return updatedSelected;
      });
    },
    [setSelectedTopicsModalSubjects, checkIsAssesmentOnly]
  );

  const getGuideCounts = async (selectedGuideSubjects) => {
    let counts = Map();
    const subjectIds = selectedGuideSubjects.map((s) => s.getId()).join(',');
    const guideTypePromises = guideTypes.map((guideType) => {
      return getGuideQuestionCountsQuery(
        subjectIds,
        guideType,
        questionDifficulties,
        questionTypes,
        selectedStandardIds,
        selectedStandardSetIds,
        hideAssessmentQuestions,
        hidePreviouslyAssigned
      ).getResourcePromise();
    });

    const guideTypeData = await Promise.all(guideTypePromises);
    guideTypes.forEach((guideType, i) => {
      const guideCount = guideTypeData[i].reduce((totalCount, guide) => {
        return totalCount + guide.getMeta().getContentDiscoveryQuestionCount();
      }, 0);
      counts = counts.set(guideType, guideCount);
    });

    setGuideCounts(counts);
  };

  useEffect(() => {
    if (selectedTopicsModalSubjects.size) {
      getGuideCounts(selectedTopicsModalSubjects);
    }
  }, [selectedTopicsModalSubjects]);

  useEffect(() => {
    // check this on mount
    checkIsAssesmentOnly(selectedTopicsModalSubjects);
  }, []);

  return (
    <Track>
      <Modal
        ariaLabel='Topics modal'
        handleClose={handleClose}
        dismissable={false}
        className='cd-modal'
      >
        {({CloseButtonWrapper}) => {
          if (!isReady) {
            return <LoadingSpinner size={3} />;
          }

          return (
            <Dialogue
              className='cd-modal__container'
              inModal
              paddingLevel={0}
              size='xl'
              hideCloseBtn
            >
              <div className='cd-modal__header'>
                <div className='cd-modal__title u-display_flex u-display_flex u-align-items_center u-pad-l_3 u-pad-r_1 u-pad-tb_1 u-border-b u-border-color_slate-300'>
                  <Text bold size='m' className='u-flex-grow_1'>
                    Find Topics
                  </Text>
                  <CloseButtonWrapper>
                    <IconButton size='s' icon='times' label='Close' onClick={handleClose} />
                  </CloseButtonWrapper>
                </div>
                <div className='u-display_flex u-display_flex u-align-items_center u-justify-items_space-between u-pad-l_3 u-pad-r_1 u-pad-tb_1 u-border-b u-border-color_slate-300'>
                  <SubjectsFilter
                    selectHandler={selectHandler}
                    selectedSubjects={selectedTopicsModalSubjects.toList()}
                    classroomIds={classroomIds}
                    onClear={() => setSelectedTopicsModalSubjects(Set())}
                  />
                  {!isEmpty ? (
                    <SearchInput
                      variant
                      value={searchString}
                      onChange={(e) => {
                        setSearchString(e.target.value);
                      }}
                      onBlur={() =>
                        searchString.length > 0 &&
                        trackEvent({
                          type: 'search bar',
                          event: 'search',
                          component: 'topics search',
                          content: searchString
                        })
                      }
                      onSubmit={onSubmit}
                      onReset={() => setSearchString('')}
                      className={classnames('u-mar-l_auto cd-modal__search-input', {
                        'cd-modal__search-input--active': searchString.length > 0
                      })}
                      placeholder='Search topic name or description'
                    />
                  ) : null}
                </div>
                {!isEmpty && (
                  <TabNavigation align='left' className='cd-topics-modal__tab-nav'>
                    {guideTypes.map((guide) => (
                      <Tab
                        as='button'
                        onClick={() => onTabSelection(guide)}
                        isActive={activeGuideType === guide}
                      >
                        <>
                          {guideTypeText[guide]}
                          {
                            /*
                              TODO: Enable for all users once feature is complete
                              See https://github.com/albert-io/nexus/issues/7059
                            */
                            sessionStore.isSuper() && (
                              <Badge
                                color={activeGuideType === guide ? 'progress' : 'passive'}
                                className='u-mar-l_1'
                                size='small'
                              >
                                {(guideCounts.get(guide) as number) || 0}
                              </Badge>
                            )
                          }
                        </>
                      </Tab>
                    ))}
                  </TabNavigation>
                )}
              </div>
              <Dialogue.Body className='cd-modal__body'>
                {!isEmpty && (
                  <SearchResults
                    guideCollection={guideCollection}
                    setGuideCollection={setGuideCollection}
                    activeGuideType={activeGuideType}
                    selectedTopicsModalSubjects={selectedTopicsModalSubjects}
                    keyword={searchString}
                  />
                )}
                {isEmpty && !loadingClassSubjects && !recentSubjects.size && (
                  <div className='cd-modal__empty-container'>
                    <div className='cd-topics-modal__empty-message-container u-bgc_white u-mar-t_3 u-pad-lr_4 u-pad-tb_6 u-border-radius_1 u-display_flex u-justify-content_center u-flex-direction_column u-text-align_center '>
                      <Text>Choose a subject from the filter above</Text>
                    </div>
                  </div>
                )}
                {isEmpty && recentSubjects.size > 0 && (
                  <div className='cd-topics-modal__recent-searches-container'>
                    <Text>
                      Suggested Subjects{' '}
                      <Text emphasis='lowest' size='s'>
                        &#40;Based on your class subjects&#41;
                      </Text>
                    </Text>
                    <ListGroup
                      as='ul'
                      border='none'
                      className='cd-topics-modal__recent-searches-list'
                    >
                      {recentSubjects.map((subject) => {
                        return (
                          <ListGroupItem
                            border={false}
                            as='li'
                            onClick={() => {
                              selectHandler(subject);
                            }}
                          >
                            <Icon icon={['far', 'clock']} className='u-mar-r_3 u-color_slate-400' />
                            {subject.getName()}
                          </ListGroupItem>
                        );
                      })}
                    </ListGroup>
                    <Text
                      emphasis='lowest'
                      size='m'
                      className='u-mar-t_3 u-text-align_center u-mar-lr_auto'
                    >
                      Search more subjects by selecting &#34;Choose Subjects&#34; above.
                    </Text>
                  </div>
                )}
              </Dialogue.Body>
              <div className='cd-modal__footer' role='group'>
                <Button
                  data-test-id='topics-modal__apply-btn'
                  onClick={() => {
                    const newGuideList = List([...getSelectedGuideLevelsInSession().toArray()]);
                    setSelectedGuideLevels(newGuideList);
                    trackEvent({type: 'button', event: 'click', content: 'apply'});
                    handleClose();
                  }}
                >
                  Apply
                </Button>
              </div>
            </Dialogue>
          );
        }}
      </Modal>
    </Track>
  );
};

export default TopicsModal;
